// Keeping a list fresh while two operators look at it.
//
// Kevin, 2026-08-13: "if two operators have the same page open and one operator creates a
// new line, the other operator doesn't see that new line currently."
//
// `refetchOnWindowFocus` was already on, which covers the operator who alt-tabs away and
// back. It does nothing for the one who simply sits on the page — the case Kevin described,
// and the common one when two people are working the same customer on a call together. So
// the shared lists poll.
//
// 45 seconds, deliberately unambitious: long enough that nobody notices the traffic, short
// enough that "I don't see it" is answered by waiting rather than by reloading. This is not
// live collaboration and should not pretend to be; when it needs to be, the org already has
// an always-on Durable Object to push from (see BACKLOG, "Two operators, one stale page").
//
// NOT WHILE A FORM IS OPEN. A refetch that re-sorts the table under an operator mid-edit is
// worse than the staleness it fixes: rows move, a kebab closes, and a half-typed form is
// suddenly attached to a different row. `refetchInterval` takes a function and react-query
// re-evaluates it each tick, so this stays true for as long as the dialog does.

export const SHARED_LIST_REFRESH_MS = 45_000;

/** Is the operator part-way through something? Any open modal counts. */
export function isFormOpen() {
  return typeof document !== 'undefined' && Boolean(document.querySelector('[role="dialog"]'));
}

/**
 * Spread into a `useQuery` for a list two operators might hold open at once.
 *
 *   useQuery({ queryKey: [...], queryFn: ..., ...sharedList() })
 *
 * `refetchIntervalInBackground: false` so a tab left open overnight is not polling a
 * dashboard nobody is looking at.
 */
export function sharedList(ms = SHARED_LIST_REFRESH_MS) {
  return {
    refetchInterval: () => (isFormOpen() ? false : ms),
    refetchIntervalInBackground: false,
  };
}
